<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { googleCalendarAPI } from '../../../../api/googleCalendar';

const { t } = useI18n();

const form = ref({ clientId: '', clientSecret: '', redirectUri: '' });
const oauthConfig = ref(null);
const loadingConfig = ref(false);
const saving = ref(false);
const saveSuccess = ref(false);
const saveError = ref('');
const authorizing = ref(false);
const authSuccess = ref(false);
const authError = ref('');

let pollInterval = null;

const tokenStatus = computed(() => {
  if (!oauthConfig.value?.hasAccessToken) {
    return {
      label: t('GOOGLE_CALENDAR.TOKEN_STATUS.UNAUTHORIZED'),
      color: 'text-slate-500',
      icon: 'i-lucide-circle-dashed',
    };
  }
  if (oauthConfig.value.tokenExpired) {
    return {
      label: t('GOOGLE_CALENDAR.TOKEN_STATUS.EXPIRED'),
      color: 'text-amber-500',
      icon: 'i-lucide-alert-circle',
    };
  }
  return {
    label: t('GOOGLE_CALENDAR.TOKEN_STATUS.VALID'),
    color: 'text-green-600',
    icon: 'i-lucide-check-circle',
  };
});

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

function startPolling() {
  let elapsed = 0;
  pollInterval = setInterval(async () => {
    elapsed += 2000;
    try {
      const res = await googleCalendarAPI.getOAuthConfig();
      if (res.data?.hasAccessToken && !res.data?.tokenExpired) {
        stopPolling();
        oauthConfig.value = res.data;
        authSuccess.value = true;
        authorizing.value = false;
        return;
      }
    } catch {
      /* continua polling */
    }

    if (elapsed >= 120000) {
      stopPolling();
      authorizing.value = false;
      authError.value = 'Tempo esgotado. Tente novamente.';
    }
  }, 2000);
}

async function fetchConfig() {
  loadingConfig.value = true;
  try {
    const res = await googleCalendarAPI.getOAuthConfig();
    oauthConfig.value = res.data;
    form.value.clientId = res.data.clientId || '';
    form.value.redirectUri = res.data.redirectUri || '';
  } catch {
    oauthConfig.value = null;
  } finally {
    loadingConfig.value = false;
  }
}

onMounted(() => fetchConfig());

async function saveConfig() {
  saving.value = true;
  saveSuccess.value = false;
  saveError.value = '';
  try {
    await googleCalendarAPI.saveOAuthConfig(form.value);
    saveSuccess.value = true;
    await fetchConfig();
    setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
  } catch {
    saveError.value = 'Erro ao salvar. Verifique os dados e tente novamente.';
  } finally {
    saving.value = false;
  }
}

async function authorizeGoogle() {
  authError.value = '';
  authSuccess.value = false;
  authorizing.value = true;

  try {
    const res = await googleCalendarAPI.getAuthorizationUrl();
    const authUrl = res.data.authorizationUrl;
    window.open(authUrl, '_blank', 'width=600,height=700,left=200,top=100');
    startPolling();
  } catch {
    authError.value =
      'Erro ao gerar URL de autorização. Verifique as credenciais.';
    authorizing.value = false;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
</script>

<template>
  <div class="flex-1 overflow-auto bg-white">
    <div class="max-w-2xl mx-auto px-6 py-8">
      <h1 class="text-xl font-semibold text-slate-800 mb-1">
        {{ t('GOOGLE_CALENDAR.TITLE') }}
      </h1>
      <p class="text-sm text-slate-500 mb-8">
        {{ t('GOOGLE_CALENDAR.DESCRIPTION') }}
      </p>

      <!-- Card: Credenciais OAuth -->
      <div class="rounded-lg border border-slate-200 p-6 mb-6">
        <h2
          class="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"
        >
          <span class="i-lucide-key-round size-4 text-slate-500" />
          {{ t('GOOGLE_CALENDAR.OAUTH_CREDENTIALS') }}
        </h2>

        <div class="space-y-4">
          <div>
            <label class="block text-xs font-medium text-slate-600 mb-1">{{
              t('GOOGLE_CALENDAR.CLIENT_ID')
            }}</label>
            <input
              v-model="form.clientId"
              type="text"
              :placeholder="t('GOOGLE_CALENDAR.CLIENT_ID_PLACEHOLDER')"
              class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-slate-600 mb-1">{{
              t('GOOGLE_CALENDAR.CLIENT_SECRET')
            }}</label>
            <input
              v-model="form.clientSecret"
              type="password"
              :placeholder="t('GOOGLE_CALENDAR.CLIENT_SECRET_PLACEHOLDER')"
              class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-slate-600 mb-1">{{
              t('GOOGLE_CALENDAR.REDIRECT_URI')
            }}</label>
            <input
              v-model="form.redirectUri"
              type="text"
              :placeholder="t('GOOGLE_CALENDAR.REDIRECT_URI_PLACEHOLDER')"
              class="w-full rounded border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
            <p class="text-xs text-slate-400 mt-1">
              {{ t('GOOGLE_CALENDAR.REDIRECT_URI_HELP') }}
            </p>
          </div>
        </div>

        <div class="mt-5 flex items-center gap-3">
          <button
            class="flex items-center gap-2 px-4 py-2 text-sm rounded bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
            :disabled="
              saving ||
              !form.clientId ||
              !form.clientSecret ||
              !form.redirectUri
            "
            @click="saveConfig"
          >
            <span
              v-if="saving"
              class="i-lucide-loader-circle size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{ t('GOOGLE_CALENDAR.SAVE_CONFIG') }}
          </button>

          <span
            v-if="saveSuccess"
            class="flex items-center gap-1 text-xs text-green-600"
          >
            <span class="i-lucide-check-circle size-3.5" />
            {{ t('GOOGLE_CALENDAR.SAVE_SUCCESS') }}
          </span>
          <span v-if="saveError" class="text-xs text-red-500">{{
            saveError
          }}</span>
        </div>
      </div>

      <!-- Card: Status de autorização -->
      <div class="rounded-lg border border-slate-200 p-6">
        <h2
          class="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"
        >
          <span class="i-lucide-shield-check size-4 text-slate-500" />
          {{ t('GOOGLE_CALENDAR.AUTHORIZATION') }}
        </h2>

        <!-- Carregando -->
        <div
          v-if="loadingConfig"
          class="flex items-center gap-2 text-sm text-slate-500"
        >
          <span class="i-lucide-loader-circle size-4 animate-spin" />
          {{ t('GOOGLE_CALENDAR.LOADING_CONFIG') }}
        </div>

        <!-- Sem configuração -->
        <div v-else-if="!oauthConfig" class="text-sm text-slate-500">
          {{ t('GOOGLE_CALENDAR.NO_CONFIG') }}
        </div>

        <!-- Com configuração -->
        <template v-else>
          <div class="space-y-2 mb-5 text-sm">
            <div class="flex items-center gap-2">
              <span class="text-slate-500 w-32 flex-shrink-0">{{
                t('GOOGLE_CALENDAR.ACCESS_TOKEN')
              }}</span>
              <span
                class="flex items-center gap-1 font-medium"
                :class="tokenStatus.color"
              >
                <span :class="tokenStatus.icon" class="size-3.5" />
                {{ tokenStatus.label }}
              </span>
            </div>
            <div v-if="oauthConfig.tokenExpiry" class="flex items-center gap-2">
              <span class="text-slate-500 w-32 flex-shrink-0">{{
                t('GOOGLE_CALENDAR.EXPIRES_AT')
              }}</span>
              <span class="text-slate-700">{{
                formatDateTime(oauthConfig.tokenExpiry)
              }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-slate-500 w-32 flex-shrink-0">{{
                t('GOOGLE_CALENDAR.CONFIGURED_AT')
              }}</span>
              <span class="text-slate-700">{{
                formatDateTime(oauthConfig.createdAt)
              }}</span>
            </div>
          </div>

          <!-- Botão autorizar -->
          <button
            class="flex items-center gap-2 px-4 py-2 text-sm rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            :disabled="authorizing"
            @click="authorizeGoogle"
          >
            <span
              v-if="authorizing"
              class="i-lucide-loader-circle size-4 animate-spin"
            />
            <svg v-else class="size-4" viewBox="0 0 48 48">
              <path
                fill="#4285F4"
                d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.3 1 7.2 2.7l5.7-5.7C33.5 7.1 29 5 24 5 13 5 4 14 4 25s9 20 20 20c11.1 0 20-8.9 20-20 0-1.2-.1-2.4-.4-3.5z"
              />
              <path
                fill="#34A853"
                d="M6.3 15.1l6.6 4.8C14.5 17 19 14 24 14c2.8 0 5.3 1 7.2 2.7l5.7-5.7C33.5 7.1 29 5 24 5 16.3 5 9.7 9.1 6.3 15.1z"
              />
              <path
                fill="#FBBC05"
                d="M24 45c5 0 9.5-1.9 12.9-5l-5.9-5c-2 1.4-4.5 2-7 2-5.3 0-9.7-2.6-11.3-7l-6.6 5.1C9.7 40.9 16.3 45 24 45z"
              />
              <path
                fill="#EA4335"
                d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l5.9 5c-.4.4 6.1-4.5 6.1-13.7 0-1.2-.1-2.4-.4-3.5z"
              />
            </svg>
            {{
              authorizing
                ? t('GOOGLE_CALENDAR.AUTHORIZING')
                : t('GOOGLE_CALENDAR.AUTHORIZE_BUTTON')
            }}
          </button>

          <p v-if="authorizing" class="text-xs text-slate-400 mt-2">
            {{ t('GOOGLE_CALENDAR.AUTHORIZING_HINT') }}
          </p>
          <p
            v-if="authSuccess"
            class="flex items-center gap-1 text-xs text-green-600 mt-2"
          >
            <span class="i-lucide-check-circle size-3.5" />
            {{ t('GOOGLE_CALENDAR.AUTH_SUCCESS') }}
          </p>
          <p v-if="authError" class="text-xs text-red-500 mt-2">
            {{ authError }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
